const HANGUL_BASE = 0xac00
const HANGUL_LAST = 0xd7a3
const JUNG_COUNT = 21
const JONG_COUNT = 28
const CHO = [...'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ']

const isHangulSyllable = (ch) => {
  const code = ch.charCodeAt(0)
  return code >= HANGUL_BASE && code <= HANGUL_LAST
}

const charSteps = (ch) => {
  if (!isHangulSyllable(ch)) return [ch]
  const code = ch.charCodeAt(0) - HANGUL_BASE
  const cho = Math.floor(code / (JUNG_COUNT * JONG_COUNT))
  const jung = Math.floor((code % (JUNG_COUNT * JONG_COUNT)) / JONG_COUNT)
  const jong = code % JONG_COUNT
  const choJung = String.fromCharCode(HANGUL_BASE + (cho * JUNG_COUNT + jung) * JONG_COUNT)
  return jong > 0 ? [CHO[cho], choJung, ch] : [CHO[cho], choJung]
}

export const commonPrefixLength = (a, b) => {
  const n = Math.min(a.length, b.length)
  let i = 0
  while (i < n && a[i] === b[i]) i += 1
  return i
}

export const frameCount = (text, chars) =>
  [...text.slice(0, chars ?? text.length)].reduce((acc, ch) => acc + charSteps(ch).length, 0)

export const typingFrames = (text) => {
  let prefix = ''
  const frames = []
  for (const ch of [...text]) {
    for (const step of charSteps(ch)) frames.push(prefix + step)
    prefix += ch
  }
  return frames
}

export const TYPING_TOTAL_MS = 1200
export const TYPING_MIN_INTERVAL_MS = 8
export const TYPING_MAX_INTERVAL_MS = 30

const isSpace = (ch) => /\s/.test(ch)

// Snap an index back to the start of its word. Spaces stay put so a mid-word
// rewrite does not keep a half-typed token as the "unchanged" prefix.
export const snapToWordStart = (text, index) => {
  if (index >= text.length || isSpace(text[index])) return index
  let n = index
  while (n > 0 && !isSpace(text[n - 1])) n -= 1
  return n
}

// Split at non-space → space boundaries. Leading whitespace stays on the
// following word so a kept gap paints with the kept word, not the rewrite.
export const tokenize = (text) => {
  const tokens = []
  let buf = ''
  let wasWord = false
  for (const ch of text) {
    const space = isSpace(ch)
    if (buf !== '' && wasWord && space) {
      tokens.push(buf)
      buf = ''
    }
    buf += ch
    wasWord = !space
  }
  if (buf !== '') tokens.push(buf)
  return tokens
}

export const diffSegments = (oldText, newText) => {
  const oldTokens = tokenize(oldText)
  const newTokens = tokenize(newText)
  const rows = oldTokens.length + 1
  const cols = newTokens.length + 1
  const lcs = Array.from({ length: rows }, () => Array(cols).fill(0))
  for (let i = oldTokens.length - 1; i >= 0; i -= 1) {
    for (let j = newTokens.length - 1; j >= 0; j -= 1) {
      lcs[i][j] =
        oldTokens[i] === newTokens[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }

  const chunks = []
  const emit = (kept, text, oldStart) => {
    const last = chunks[chunks.length - 1]
    if (last && last.kept === kept) last.text += text
    else chunks.push({ kept, text, oldStart })
  }

  let i = 0
  let j = 0
  let oldStart = 0
  while (i < oldTokens.length && j < newTokens.length) {
    if (oldTokens[i] === newTokens[j] && lcs[i][j] === lcs[i + 1][j + 1] + 1) {
      emit(true, oldTokens[i], oldStart)
      oldStart += oldTokens[i].length
      i += 1
      j += 1
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      oldStart += oldTokens[i].length
      i += 1
    } else {
      emit(false, newTokens[j], oldStart)
      j += 1
    }
  }
  while (j < newTokens.length) {
    emit(false, newTokens[j], oldStart)
    j += 1
  }
  return chunks
}

const seedChunk = (text, chars) => ({
  text,
  idx: frameCount(text, chars)
})

const leftoverPace = (chunks) =>
  chunks.reduce((max, chunk) => Math.max(max, frameCount(chunk.text) - chunk.idx), 0)

export const createTypingState = (text = '') => ({
  target: text,
  chunks: text ? [{ text, idx: 0 }] : [],
  pace: frameCount(text)
})

export const shownChunk = (chunk) => {
  if (chunk.idx === 0) return ''
  const frames = typingFrames(chunk.text)
  return frames[Math.min(chunk.idx, frames.length) - 1] ?? ''
}

export const shownText = (state) => state.chunks.map(shownChunk).join('')

export const remainingFrames = (state) =>
  state.chunks.reduce((sum, chunk) => {
    const total = typingFrames(chunk.text).length
    return sum + Math.max(0, total - chunk.idx)
  }, 0)

export const typingInterval = (state) =>
  Math.min(
    TYPING_MAX_INTERVAL_MS,
    Math.max(TYPING_MIN_INTERVAL_MS, TYPING_TOTAL_MS / Math.max(1, state.pace))
  )

export const tickTyping = (state) => {
  let moved = false
  const chunks = state.chunks.map((chunk) => {
    const total = typingFrames(chunk.text).length
    if (chunk.idx >= total) return chunk
    moved = true
    return { ...chunk, idx: chunk.idx + 1 }
  })
  return moved ? { ...state, chunks } : state
}

const reachedChars = (state) => {
  let reached = 0
  let visible = ''
  let stillPrefix = true
  state.chunks.forEach((chunk) => {
    const shown = shownChunk(chunk)
    visible += shown
    if (!stillPrefix) return
    if (shown === chunk.text) {
      reached += chunk.text.length
    } else {
      reached += commonPrefixLength(shown, chunk.text)
      stillPrefix = false
    }
  })
  return { reached, visible }
}

export const applyTextChange = (state, text, keepTail = true) => {
  if (state.target === text) return state
  if (!text) return createTypingState('')

  const { reached, visible } = reachedChars(state)
  const prefix = commonPrefixLength(visible, text)
  let chunks

  if (prefix + 1 >= visible.length) {
    chunks = [seedChunk(text.slice(0, prefix), prefix), { text: text.slice(prefix), idx: 0 }].filter(
      (chunk) => chunk.text !== ''
    )
  } else if (keepTail) {
    const segs = diffSegments(state.target, text)
    const changed = segs.filter((seg) => !seg.kept).length
    if (changed >= 2) {
      chunks = [seedChunk(text, text.length)]
    } else {
      chunks = segs
        .map((seg) =>
          seg.kept
            ? seedChunk(seg.text, Math.max(0, Math.min(seg.text.length, reached - seg.oldStart)))
            : { text: seg.text, idx: 0 }
        )
        .filter((chunk) => chunk.text !== '')
      if (chunks.length === 0) chunks = [{ text, idx: 0 }]
    }
  } else {
    const kept = text.slice(0, snapToWordStart(text, prefix))
    chunks = [seedChunk(kept, kept.length), { text: text.slice(kept.length), idx: 0 }].filter(
      (chunk) => chunk.text !== ''
    )
  }

  return { target: text, chunks, pace: leftoverPace(chunks) }
}
